import { Button, Skeleton, cn } from "@ndd/ui";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  RotateCw,
  WifiOff,
  X,
} from "lucide-react";
import * as React from "react";
import type {
  AppointmentPreviousSoapNoteContent,
  AppointmentPreviousSoapNoteListed,
} from "../../../../shared/api-types";
import {
  activeSoapSection,
  formatNoteDate,
  jumpBarItems,
  pagerState,
  signedAtLabel,
  type JumpBarItem,
} from "../../lib/previous-soap-notes-reader";
import { relativeTimeLabel } from "../../lib/relative-time";
import {
  scrollToSoapNoteSection,
  splitSoapNoteSections,
  type SoapNoteSection,
  type SoapSectionLabel,
} from "../../lib/soap-note-sections";
import { DRAWER_TEXT, NOTE_BLOCK } from "./drawer-type-roles";
import { PriorSoapNoteMarkdown } from "./prior-soap-note-markdown";
import type { PreviousSoapNoteSelectionView } from "./use-previous-soap-notes";
import {
  UTILITY_ENGAGED_FILL,
  UTILITY_REST,
  WIDEN_PRESSED_EDGE,
} from "./workspace-look";

/** How far below the scroll top a heading may sit and still own the highlight. */
const SCROLL_SPY_OFFSET = 24;

export interface PreviousSoapNotesReaderProps {
  notes: AppointmentPreviousSoapNoteListed[];
  index: number;
  selection: PreviousSoapNoteSelectionView;
  timezone: string;
  now: string;
  onBack: () => void;
  onNewer: () => void;
  onOlder: () => void;
  onRetrySelection: () => void;
  /** Present when this drawer owns its close control (the docked drawer). */
  onClose?: () => void;
  /** Extra reader-bar controls, placed before the close button. */
  controls?: React.ReactNode;
  /** The date heading, which takes focus when a visit opens or the pager moves. */
  dateRef?: React.Ref<HTMLHeadingElement>;
}

/** One visit: reader bar, date and pager, a pinned jump bar, then the note. */
export function PreviousSoapNotesReader(
  props: PreviousSoapNotesReaderProps
): React.ReactElement {
  const note = props.notes[props.index];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ReaderBar
        onBack={props.onBack}
        onClose={props.onClose}
        controls={props.controls}
      />
      {note ? <ReaderHeader {...props} note={note} /> : null}
      <ReaderBody
        selection={props.selection}
        onRetrySelection={props.onRetrySelection}
      />
    </div>
  );
}

function ReaderBar({
  onBack,
  onClose,
  controls,
}: Pick<
  PreviousSoapNotesReaderProps,
  "onBack" | "onClose" | "controls"
>): React.ReactElement {
  return (
    <div className="flex shrink-0 items-center justify-between px-3.5 pt-2">
      <Button type="button" variant="ghost" size="xs" onClick={onBack}>
        <ArrowLeft aria-hidden="true" />
        <span className="text-body leading-4 font-medium text-foreground">
          All notes
        </span>
      </Button>
      <div className="flex items-center gap-0.5">
        {controls}
        {onClose ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Close previous SOAP notes"
            onClick={onClose}
            className="text-muted-foreground"
          >
            <X aria-hidden="true" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function ReaderHeader({
  note,
  notes,
  index,
  timezone,
  now,
  onNewer,
  onOlder,
  dateRef,
}: PreviousSoapNotesReaderProps & {
  note: AppointmentPreviousSoapNoteListed;
}): React.ReactElement {
  return (
    <div className="flex shrink-0 flex-col pb-2.5 pl-5 pr-3 pt-1.5">
      <div className="flex items-center justify-between gap-2">
        <h3
          ref={dateRef}
          tabIndex={-1}
          className={cn(DRAWER_TEXT.readerDate, "outline-none")}
        >
          {formatNoteDate(note.serviceDateAt, timezone)}
        </h3>
        <Pager index={index} total={notes.length} onNewer={onNewer} onOlder={onOlder} />
      </div>
      <p className={DRAWER_TEXT.meta}>
        {signedAtLabel(note.signedAtBasis)} {formatNoteDate(note.signedAt, timezone)} ·{" "}
        {relativeTimeLabel(note.serviceDateAt, now)}
      </p>
    </div>
  );
}

const PAGER_BUTTON = "px-2.5 hover:bg-accent disabled:opacity-40";

function Pager({
  index,
  total,
  onNewer,
  onOlder,
}: {
  index: number;
  total: number;
  onNewer: () => void;
  onOlder: () => void;
}): React.ReactElement {
  const pager = pagerState(index, total);

  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <Button
        type="button"
        variant="ghost"
        size="xs"
        disabled={pager.newerDisabled}
        onClick={onNewer}
        className={PAGER_BUTTON}
      >
        <ChevronLeft aria-hidden="true" />
        <span className={DRAWER_TEXT.control}>Newer</span>
      </Button>
      <span className={cn(DRAWER_TEXT.count, "px-1.5")}>{pager.label}</span>
      <Button
        type="button"
        variant="ghost"
        size="xs"
        disabled={pager.olderDisabled}
        onClick={onOlder}
        className={PAGER_BUTTON}
      >
        <span className={DRAWER_TEXT.control}>Older</span>
        <ChevronRight aria-hidden="true" />
      </Button>
    </div>
  );
}

function ReaderBody({
  selection,
  onRetrySelection,
}: {
  selection: PreviousSoapNoteSelectionView;
  onRetrySelection: () => void;
}): React.ReactElement {
  if (selection.status === "loading") {
    return (
      <div className="flex-1 border-t border-border px-5 pt-5" role="status">
        <div className="space-y-3">
          <Skeleton className="h-2.5 w-11/12" />
          <Skeleton className="h-2.5 w-3/4" />
          <Skeleton className="h-2.5 w-full" />
          <Skeleton className="h-2.5 w-3/5" />
        </div>
        <p className={cn(DRAWER_TEXT.stateBody, "pt-5")}>Loading this note…</p>
      </div>
    );
  }

  if (selection.status === "unavailable") {
    return (
      <div className="flex-1 border-t border-border">
        <div className="flex flex-col items-center px-6 py-7 text-center">
          <span className="flex size-9 items-center justify-center rounded-full bg-muted">
            <WifiOff aria-hidden="true" className="size-4 text-muted-foreground" />
          </span>
          <p role="status" className={cn(DRAWER_TEXT.stateTitle, "mt-3")}>
            Couldn’t load this note
          </p>
          <p className={cn(DRAWER_TEXT.stateBody, "mt-1")}>
            Healthie didn’t respond. Use All notes or Newer / Older to read your other
            visits.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={onRetrySelection}
          >
            <RotateCw aria-hidden="true" />
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return <ReaderNote key={selection.note.id} note={selection.note} />;
}

function readHeadingTops(
  container: HTMLElement,
  sections: SoapNoteSection[],
  elements: Map<string, HTMLElement>
): { top: number; soap: SoapSectionLabel | null }[] {
  const base = container.getBoundingClientRect().top - container.scrollTop;
  const tops: { top: number; soap: SoapSectionLabel | null }[] = [];

  for (const section of sections) {
    const element = elements.get(section.key);
    if (element)
      tops.push({
        top: element.getBoundingClientRect().top - base,
        soap: section.soap,
      });
  }

  return tops;
}

function useSoapScrollSpy(input: {
  items: JumpBarItem[];
  sections: SoapNoteSection[];
  scrollRef: React.RefObject<HTMLDivElement | null>;
  headingRefs: React.RefObject<Map<string, HTMLElement>>;
}): [SoapSectionLabel | null, (label: SoapSectionLabel | null) => void] {
  const [active, setActive] = React.useState<SoapSectionLabel | null>(null);
  const { items, sections, scrollRef, headingRefs } = input;

  React.useEffect(() => {
    const container = scrollRef.current;
    if (!container || items.length === 0) return;

    let frame = 0;

    const update = (): void => {
      frame = 0;
      const tops = readHeadingTops(container, sections, headingRefs.current);
      setActive(activeSoapSection(tops, container.scrollTop + SCROLL_SPY_OFFSET));
    };

    const onScroll = (): void => {
      if (frame === 0) frame = requestAnimationFrame(update);
    };

    update();
    container.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", onScroll);
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, [items, sections, scrollRef, headingRefs]);

  return [active, setActive];
}

function ReaderNote({
  note,
}: {
  note: AppointmentPreviousSoapNoteContent;
}): React.ReactElement {
  const split = React.useMemo(
    () => splitSoapNoteSections(note.content),
    [note.content]
  );
  const items = React.useMemo(() => jumpBarItems(split.sections), [split.sections]);
  const idPrefix = React.useId();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const headingRefs = React.useRef(new Map<string, HTMLElement>());
  const [active, setActive] = useSoapScrollSpy({
    items,
    sections: split.sections,
    scrollRef,
    headingRefs,
  });

  function jumpTo(item: JumpBarItem): void {
    const target = headingRefs.current.get(item.key);
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!scrollToSoapNoteSection(target, reduceMotion)) return;

    setActive(item.label);
    target?.focus({ preventScroll: true });
  }

  return (
    <>
      {items.length > 0 ? (
        <JumpBar items={items} active={active} idPrefix={idPrefix} onJump={jumpTo} />
      ) : (
        <div className="h-px shrink-0 bg-border" />
      )}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-5 pb-12 pt-1">
        {split.intro ? <PriorSoapNoteMarkdown markdown={split.intro} /> : null}
        {split.sections.map((section) => (
          <React.Fragment key={section.key}>
            <h3
              id={`${idPrefix}-${section.key}`}
              tabIndex={-1}
              ref={(element) => {
                if (element) headingRefs.current.set(section.key, element);
                else headingRefs.current.delete(section.key);
              }}
              className={cn(
                DRAWER_TEXT.sectionHeading,
                NOTE_BLOCK.sectionHeading,
                "outline-none"
              )}
            >
              {section.heading}
            </h3>
            {section.text ? <PriorSoapNoteMarkdown markdown={section.text} /> : null}
          </React.Fragment>
        ))}
      </div>
    </>
  );
}

export function JumpBar({
  items,
  active,
  idPrefix,
  onJump,
}: {
  items: JumpBarItem[];
  active: SoapSectionLabel | null;
  idPrefix: string;
  onJump: (item: JumpBarItem) => void;
}): React.ReactElement {
  return (
    <nav
      aria-label="Jump to section"
      className="flex shrink-0 flex-nowrap gap-0.5 overflow-x-auto whitespace-nowrap border-y border-border bg-card px-3.5 py-1.5"
    >
      {items.map((item) => {
        const current = active === item.label;

        return (
          <button
            key={item.key}
            type="button"
            onClick={() => onJump(item)}
            aria-controls={`${idPrefix}-${item.key}`}
            aria-current={current ? "true" : undefined}
            className={cn(
              "shrink-0 rounded-sm border-b-2 px-2.5 py-[3px] text-body-sm leading-[18px] font-medium text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              current
                ? "border-sand-600 bg-accent"
                : "border-transparent bg-card hover:bg-muted"
            )}
          >
            {item.label}
          </button>
        );
      })}
    </nav>
  );
}

// Button variant utility (NO-799): classic the outline button as before; under
// the theme card-white with an olive inset while pressed (workspace-look.ts).
const WIDTH_BUTTON = "gap-2 px-2.5 text-body-sm leading-[18px] text-foreground";

/** Widen at rest; Narrow, pressed, while the drawer is wide. A class change only. */
export function WidenButton({
  wide,
  onToggle,
}: {
  wide: boolean;
  onToggle: () => void;
}): React.ReactElement {
  const Icon = wide ? Minimize2 : Maximize2;

  return (
    <Button
      type="button"
      variant="utility"
      size="xs"
      aria-pressed={wide}
      onClick={onToggle}
      className={cn(
        WIDTH_BUTTON,
        wide
          ? cn(WIDEN_PRESSED_EDGE, UTILITY_ENGAGED_FILL)
          : cn(UTILITY_REST, "hover:bg-muted")
      )}
    >
      <Icon aria-hidden="true" />
      {wide ? "Narrow" : "Widen"}
    </Button>
  );
}
